import type { ComponentType as ReactComponentType } from 'react'
import type { ComponentType } from '@/types/schemas/workspace'
import type { PanelArtProps } from './panel-art-props'
import { REALTIME_WEBSOCKET_ENABLED } from '@/services/realtime/flags'
import { BarCategoryArt } from './bar-category-art'
import { CalendarArt } from './calendar-art'
import { CalendarHeatmapArt } from './calendar-heatmap-art'
import { ChartArt } from './chart-art'
import { BarChartArt } from './bar-chart-art'
import { ComparisonArt } from './comparison-art'
import { DonutCategoryArt } from './donut-category-art'
import { FlowGraphArt } from './flow-graph-art'
import { HistoryChartArt } from './history-chart-art'
import { HistoryHeatmapArt } from './history-heatmap-art'
import { KeyStatsArt } from './key-stats-art'
import { MarketOverwatchArt } from './market-overwatch-art'
import { MarketStatsArt } from './market-stats-art'
import { MultiChartArt } from './multi-chart-art'
import { MultiScannerArt } from './multi-scanner-art'
import { NewsArt } from './news-art'
import { NotebookArt } from './notebook-art'
import { PriceActionArt } from './price-action-art'
import { PriceAlertsArt } from './price-alerts-art'
import { AlertWindowsArt } from './alert-windows-art'
import { RecordsArt } from './records-art'
import { ScatterArt } from './scatter-art'
import { ScorecardArt } from './scorecard-art'
import { SectorHeatmapArt } from './sector-heatmap-art'
import { TagsArt } from './tags-art'
import { ThemeTrackerArt } from './theme-tracker-art'
import { TimeSeriesArt } from './time-series-art'
import { TimingBreakdownArt } from './timing-breakdown-art'
import { WatchlistArt } from './watchlist-art'
import { ScannerParityArt } from './scanner-parity-art'

export type { PanelArtProps }

/**
 * The illustration shown on each card of the visual Add Panel hub, keyed by
 * component type.
 *
 * Partial by design: only the panel types the dashboard and History menus offer
 * (`DASHBOARD_TYPES` and `HISTORY_TYPES`) are drawn. Backtest and run panels
 * fall back to their lucide icon. `index.unit.test.tsx` guards the pairing in
 * both directions, so adding a type to either menu without art fails the suite
 * rather than shipping an empty card.
 */
export const PANEL_CARD_ART: Partial<Record<ComponentType, ReactComponentType<PanelArtProps>>> = {
  // The Records drawing reads better as a scanner too (Chris, 2026-09-16).
  scanner: RecordsArt,
  'multi-scanner': MultiScannerArt,
  watchlist: WatchlistArt,
  // Same panel as Market Stats on the History page, so the same picture.
  'ticker-stats': MarketStatsArt,
  chart: ChartArt,
  'multi-chart': MultiChartArt,
  'market-overwatch': MarketOverwatchArt,
  'sector-heatmap': SectorHeatmapArt,
  'theme-tracker': ThemeTrackerArt,
  'key-stats': KeyStatsArt,
  calendar: CalendarArt,
  news: NewsArt,
  notebook: NotebookArt,
  ...(REALTIME_WEBSOCKET_ENABLED
    ? { 'price-alerts': PriceAlertsArt, 'alert-windows': AlertWindowsArt }
    : {}),
  'scanner-parity': ScannerParityArt,
  // ── History ──
  'history-records': RecordsArt,
  'history-chart': HistoryChartArt,
  'history-multi-chart': MultiChartArt,
  'history-stats': MarketStatsArt,
  'history-news': NewsArt,
  'history-comparison': ComparisonArt,
  'history-time-series': TimeSeriesArt,
  'history-scatter': ScatterArt,
  'history-bar': BarChartArt,
  'history-bar-category': BarCategoryArt,
  'history-donut-category': DonutCategoryArt,
  'history-intraday': PriceActionArt,
  'history-calendar': CalendarHeatmapArt,
  'history-sankey': FlowGraphArt,
  'history-scorecard': ScorecardArt,
  'history-heatmap': HistoryHeatmapArt,
  'history-time-distribution': TimingBreakdownArt,
  'tags-explorer': TagsArt,
}
